import { LDateTime } from '@app/components/date/LDateTime';
import {
  diffLabelMessage,
  toleranceHelpMessage,
  toleranceLabelMessage,
} from '@app/modules/audit/modules/station-audit/components/schedule/schedule-match-details.messages';
import { ScheduleOffToleranceWarning } from '@app/modules/audit/modules/station-audit/components/schedule/ScheduleOffToleranceWarning';
import { ScheduleTolerance } from '@app/modules/audit/modules/station-audit/components/schedule/ScheduleTolerance';
import { AuditSchedule } from '@mi-front/core';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Tooltip, Typography } from '@mui/material';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

const matchDatetimeLabelMessage = defineMessage({
  id: 'audit.schedulesList.matchDatetime',
  defaultMessage: 'Match',
});

type Props = {
  schedule: AuditSchedule;
  /** Full match datetime shown when a match exists. */
  matchDatetime: string | null;
  /** Match vs schedule difference in minutes. Omitted when there is no match. */
  diffMinutes: number | null;
  isOffTolerance: boolean;
};

/**
 * Popover body for schedule match status: full match datetime, tolerance and diff.
 */
export function ScheduleMatchStatusDetails({
  schedule,
  matchDatetime,
  diffMinutes,
  isOffTolerance,
}: Props) {
  const { $t } = useIntl();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {matchDatetime && (
        <Typography variant="body2">
          <FormattedMessage {...matchDatetimeLabelMessage} />
          {': '}
          <LDateTime value={matchDatetime} showSeconds />
        </Typography>
      )}

      <Typography
        variant="body2"
        component="div"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
      >
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <FormattedMessage {...toleranceLabelMessage} />
          <Tooltip title={$t(toleranceHelpMessage)}>
            <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
              <HelpOutlineIcon fontSize="small" />
            </Box>
          </Tooltip>
        </Box>
        {': '}
        <ScheduleTolerance config={schedule.toleranceConfig} />
      </Typography>

      {diffMinutes != null && (
        <Typography variant="body2">
          <FormattedMessage {...diffLabelMessage} />
          {`: ${diffMinutes.toFixed(1)} min`}
        </Typography>
      )}

      {isOffTolerance && <ScheduleOffToleranceWarning />}
    </Box>
  );
}
